<template>
  <v-card class="pa-4">
    <h2>Outstanding Assignment by Days (Insurer)</h2>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedDept"
          :items="deptOptions"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedClass"
          :items="filteredClassOptions"
          item-text="subCode"
          item-value="id"
          label="Classification"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-menu v-model="menuDate" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedDate"
              label="Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedDate"
            @change="menuDate = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="!selectedDate"
        >
          Generate Report
        </v-btn>
        <v-btn
          color="success"
          class="ml-2"
          @click="exportExcel"
          :loading="exporting"
          :disabled="!reportData.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export to Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <div v-if="reportData.length">
      <v-simple-table>
        <thead>
          <tr>
            <th>Insurer</th>
            <th>Outstanding Below 30 Days</th>
            <th>Outstanding Above 30 Days</th>
            <th>Outstanding Above 45 Days</th>
            <th>Outstanding Above 60 Days</th>
            <th>Outstanding Above 90 Days</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in reportData">
            <tr :key="row.insurerCode">
              <td>
                <strong>{{ row.insurerCode }}</strong>
              </td>
              <td>{{ row.below30 }}</td>
              <td>{{ row.above30 }}</td>
              <td>{{ row.above45 }}</td>
              <td>{{ row.above60 }}</td>
              <td>{{ row.above90 }}</td>
              <td>{{ row.total }}</td>
            </tr>
            <tr
              v-for="detail in row.details"
              :key="row.insurerCode + '-' + detail.dept + '-' + detail.class"
              class="grey--text text--darken-2"
            >
              <td>
                <span style="padding-left: 24px">
                  {{ row.insurerCode }} ({{ detail.dept }} - {{ detail.class }})
                </span>
              </td>
              <td>{{ detail.below30 }}</td>
              <td>{{ detail.above30 }}</td>
              <td>{{ detail.above45 }}</td>
              <td>{{ detail.above60 }}</td>
              <td>{{ detail.above90 }}</td>
              <td>{{ detail.total }}</td>
            </tr>
          </template>
          <tr>
            <td><strong>TOTAL</strong></td>
            <td>
              <strong>{{ totals.below30 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above30 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above45 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above60 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above90 }}</strong>
            </td>
            <td>
              <strong>{{ totals.total }}</strong>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-alert v-else-if="noDataMessage" type="warning" outlined class="mt-4">
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
          Please select department, classification, and date, then click "Generate
      Report" to view data.
    </v-alert>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import DeptDataService from "../../services/DeptDataService";
import SubDeptDataService from "../../services/SubDeptDataService";
import { apiConfig } from "../../config/api.config";

export default {
  name: "OutstandingDaysInsurer",
  data() {
    return {
      selectedDept: "all",
      selectedClass: "all",
      selectedDate: null,
      menuDate: false,
      reportData: [],
      totals: {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      },
      exporting: false,
      noDataMessage: "",
      deptOptions: [],
      classOptions: [],
      filteredClassOptions: [],
    };
  },
  watch: {
    selectedDept(newDept) {
      this.filterClassifications(newDept);
      this.selectedClass = "all";
    },
    classOptions: {
      handler() {
        this.filterClassifications(this.selectedDept);
      },
      immediate: true,
    },
  },
  methods: {
    async loadDepartments() {
      const res = await DeptDataService.getAll();
      this.deptOptions = [{ id: "all", name: "ALL DEPARTMENTS" }, ...res.data];
    },
    async loadClassifications() {
      const res = await SubDeptDataService.getAll();
      this.classOptions = [
        { id: "all", subCode: "ALL CLASSIFICATIONS", deptId: "all" },
        ...res.data,
      ];
    },
    filterClassifications(deptId) {
      // Only show ALL CLASSIFICATIONS if ALL DEPARTMENTS is selected
      if (deptId === "all") {
        this.filteredClassOptions = this.classOptions.filter(
          (c) => c.id === "all"
        );
      } else {
        const allClass = this.classOptions.find((c) => c.id === "all");
        const filtered = this.classOptions.filter((c) => c.deptId === deptId);
        this.filteredClassOptions = [allClass, ...filtered].filter(Boolean);
      }
    },
    async generateReport() {
      this.reportData = [];
      this.totals = {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      };
      this.noDataMessage = "";
      try {
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          date: moment(this.selectedDate).format("YYYY-MM-DD"),
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/outstanding-days-insurer`,
          { params }
        );
        this.processReportData(res.data);
        if (
          !Array.isArray(res.data.reportData) ||
          res.data.reportData.length === 0
        ) {
          const dateStr = moment(this.selectedDate).format("DD MMMM YYYY");
          this.noDataMessage = `No outstanding data found as of ${dateStr}.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    processReportData(data) {
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      const totals = {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      };
      this.reportData.forEach((row) => {
        totals.below30 += row.below30;
        totals.above30 += row.above30;
        totals.above45 += row.above45;
        totals.above60 += row.above60;
        totals.above90 += row.above90;
        totals.total += row.total;
      });
      this.totals = totals;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          date: moment(this.selectedDate).format("YYYY-MM-DD"),
        };
        const url =
          `${apiConfig.baseURL}/compliance/outstanding-days-insurer/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.loadDepartments();
    this.loadClassifications();
  },
};
</script>
